import * as React from "react";
import HcpLayout from "../components/layout";
import FluidContent from "../components/fluidContent";
import BannerInner from "../components/banner/banner-inner";
import bannerBg from "../images/pipeline/pipeline-banner-bg.jpg";
import { Col, Row } from "react-grid-system";
import CardDataInfo from "../components/cards/cardDataInfo";
import { Seo } from "../components/seo";
import Button from "../components/button";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { StaticImage } from "gatsby-plugin-image";
import ResponsiveTabs, { Tab, TabList, TabPanel } from "../components/custom/responsive-tabs";
import CardTile from "../components/cards/cardTile";
import CurvedShape from "../components/curved-shape";
import magentaBg from "../images/magenta-bg.jpg";
import { Link } from "gatsby";

const MotixafortideHscPage = () => {

  return (
    <HcpLayout pageClass="stem-cell-mobilization-for-multiple-myeloma-page pipeline-sub-pages">
      <FluidContent className="hero-banner">
        <BannerInner bannerBg={bannerBg}>
          <h2 className="text-primary">
            <p>Stem Cell Mobilization for <strong>Multiple Myeloma</strong></p>
          </h2>
        </BannerInner>
      </FluidContent>

      <FluidContent className="page-content page-content-1 block-page-content">
        <div className="centered-content">
          <div className="text-wrap">
            <p>Multiple myeloma, the second most-common blood cancer, is a cancer in which malignment plasma cells build up in the bone marrow, crowding out healthy blood cells and preventing the production of properly functioning antibodies.</p>
            <p>Autologous stem cell transplantation (ASCT) is the preferred first-line treatment for multiple myeloma,<sup>1</sup> and integral to the prospect of improving survival and helping to restore the immune system.</p>
            <p>Prior to ASCT, patients undergo apheresis, a procedure to collect stem cells for the transplant. Stem cells need to be mobilized, or moved, from the bone marrow to the bloodstream for collection during apheresis. </p>
            <p>ASCT success depends on adequate mobilization and collection of stem cells.<sup>2</sup> While more multiple myeloma patients are candidates for ASCT than ever before,<sup>3</sup> stem cell mobilization and collection is a growing challenge.</p>
            <ul>
              <li>
                <strong>Increasing age</strong> makes it increasingly difficult to collect enough stem cells for ASCT<sup>4</sup>
                <ul>
                  <li>The proportion of older patients receiving ASCT has increased over the last decade, with 38% of patients aged {">"}65 years in 2021<sup>5</sup>*</li>
                </ul>
              </li>
              <li>
                <strong>Standard 3- and 4-drug induction therapy</strong> can further impair mobilization, with fewer cells mobilized and additional days of apheresis required<sup>6,7</sup>
              </li>
            </ul>
            <div className="footnote">
              <span className="asteric">*</span>This includes MM/plasma cell disorders, non-Hodgkin lymphoma, and Hodgkin lymphoma.<sup>8</sup>
            </div>

          </div>
        </div>
      </FluidContent>

      <FluidContent className="block-factors">
        <div className="centered-content">
          <h5>Factors to consider:</h5>
          <Row className="row">
            <Col md={6} className="col">
              <CardDataInfo className="card-datainfo--alt">
                <div className="icon-info-wrap">
                  <div className="info">
                    <h2>
                      <span>Up to <span className="number">47%</span> of patients</span>
                    </h2>
                    <p className="">
                      have had challenges collecting target numbers of stem cells within one apheresis session, depending on induction regimens and mobilization strategies<sup>8,9</sup>
                    </p>
                  </div>
                </div>
              </CardDataInfo>
            </Col>
            <Col md={6} className="col">
              <CardDataInfo className="card-datainfo__list">
                <div className="icon-info-wrap">
                  <div className="info">
                    <p>
                      <strong>
                        Multiple apheresis sessions can lead to:
                      </strong>
                    </p>
                    <ul>
                      <li>More adverse events</li>
                      <li>Higher costs</li>
                      <li>Inconvenience for patients and transplant center administrators<sup>10</sup></li>
                    </ul>
                  </div>
                </div>
              </CardDataInfo>
            </Col>
          </Row>
          <Row className="row">
            <Col>
              <CardDataInfo className="text-center">
                <div className="icon-info-wrap">
                  <div className="info">
                    <p className="">
                      <strong>
                        As age and use of induction therapy regimens increase, the majority of patients will require CXCR4 antagonist mobilization either upfront or as rescue for ASCT
                      </strong><sup>7,11</sup>
                    </p>
                  </div>
                </div>
              </CardDataInfo>
            </Col>
          </Row>
        </div>
      </FluidContent>

      <FluidContent className="block-myeloma">
        <div className="centered-content">
          <div className="text-wrap">
            <h4>Motixafortide in Multiple Myeloma</h4>
            <p>In September 2023, the U.S. Food and Drug Administration (FDA) approved motixafortide – a CXCR4 antagonist with long receptor occupancy<sup>12</sup> – in combination with filgrastim (G-CSF) to mobilize hematopoietic stem cells to the peripheral blood for collection and subsequent autologous transplantation in patients with multiple myeloma.</p>
            <p>The FDA approval of motixafortide was based on results from the 2-part, Phase 3 GENESIS trial, a randomized, double-blind, placebo-controlled study. Data from the GENESIS trial was published in the peer-reviewed journal <i>Nature Medicine</i> and included patients representative of current multiple myeloma populations undergoing hemopoietic stem cell transplant (HSCT), including older patients and those who received lenalidomide-containing induction therapies – factors associated with impaired HSTC mobilization.
            </p>
            <p>
              <strong>To see additional resources for healthcare providers, please see our product page</strong>
            </p>
            <div className="btn-wrap btn-group">
              <Link
                to="/product"
                className="btn btn-primary has-icon-right">
                View Resources
                <FontAwesomeIcon icon={faArrowRight} />
              </Link>
            </div>
          </div>
        </div>
      </FluidContent>

      <FluidContent className="how-it-work__block lightBlue-overlay">
        <StaticImage
          className="top-curve"
          src="../images/top-right-white-curved.png"
          alt="White Curve Shape"
          placeholder="none"
          loading="lazy"
          objectFit="initial"
          quality={100}
        />
        <StaticImage
          className="bottom-curve"
          src="../images/white-curve.png"
          alt="White Curve Shape"
          placeholder="none"
          loading="lazy"
          objectFit="initial"
          quality={100}
        />
        <div className="centered-content block-container">
          <div className="block-content">
            <h4 className="mb-20">
              How Motixafortide Works
            </h4>
            <p>Motixafortide, a CXCR4 antagonist with long receptor occupancy (greater than 72 hours), is a 14-amino-acid synthetic cyclic peptide. It blocks CXCR4 on hematopoietic stem cells (HSC) from binding to its ligand, CXCL12, that retains HSC in the bone marrow and lymph nodes. This blocking releases the HSC to the peripheral blood, where they are available for collection during apheresis.</p>
          </div>
          <div className="block-media">
            <h5>Stem Cell Mobilization</h5>
            <StaticImage
              className="desktop-only"
              src="../images/motixafortide-stem-cell-mobilization-deskto.png"
              alt="Scientific illustration depicting how motixafortide works in stem cell mobilization"
              placeholder="none"
              quality={100}
              layout="constrained"
            />
          </div>
        </div>

        <div className="media-block px-1">
          <StaticImage
            className="mobile-only"
            layout="constrained"
            src="../images/motixafortide-stem-cell-mobilization-mobile.webp"
            alt="Scientific illustration depicting how motixafortide works in stem cell mobilization"
            placeholder="none"
            quality={100}
          />
        </div>
      </FluidContent>

      <FluidContent className="sickle-block" sid="sickle-cell">
        <div className="centered-content">
          <div className="block-title">
            <h4>
              Announcements & Studies
            </h4>
          </div>

          <div className="sickle-block__resources">
            <ResponsiveTabs>
              <TabList>
                <Tab>PRESS RELEASES</Tab>
                <Tab>POSTERS & PRESENTATIONS</Tab>
                <Tab>PUBLICATIONS</Tab>
              </TabList>

              <TabPanel>
                <Row>
                  <Col lg={3} md={5} sm={6} className="two-col">
                    <CardTile
                      url={"https://ir.biolinerx.com/news-releases/news-release-details/biolinerx-announces-presentations-cost-effectiveness"}
                      type={"PRESS RELEASE"}
                      title={"ASH 2022"}
                    />
                  </Col>
                  <Col lg={4.5} md={7} sm={6} className="two-col">
                    <CardTile
                      url={"https://ir.biolinerx.com/news-releases/news-release-details/biolinerx-announces-publication-nature-medicine-its-genesis"}
                      type={"PRESS RELEASE"}
                      title={"GENESIS Phase 3 Clinical Trial Data Published in <span class='italics'>Nature Medicine</span>"}
                    />
                  </Col>
                  <Col lg={3} md={5} sm={6} className="two-col">
                    <CardTile
                      url={"https://ir.biolinerx.com/news-releases/news-release-details/biolinerx-announces-fda-approval-aphexdatm-motixafortide"}
                      type={"PRESS RELEASE"}
                      title={" U.S. FDA Approval"}
                    />
                  </Col>
                </Row>
              </TabPanel>

              <TabPanel>
                <Row>
                  <Col lg={4} sm={6} className="two-col">
                    <CardTile
                      url={"https://www.ispor.org/docs/default-source/intl2024/240318motixafortidemodelisporfinal-2024-04-26---48x36-inchespdf136762-pdf.pdf?sfvrsn=cd276ac8_0"}
                      type={"POSTER"}
                      title={"ISPOR 2024"}
                    />
                  </Col>
                  <Col lg={3} sm={6} className="two-col">
                    <CardTile
                      url={"https://onlinelibrary.wiley.com/doi/abs/10.1002/jca.22113"}
                      type={"POSTER"}
                      title={"ASFA 2024 Abstract"}
                    />
                  </Col>
                  <Col lg={4} sm={6} className="two-col">
                    <CardTile
                      url={"https://tandem.confex.com/tandem/2024/meetingapp.cgi/Paper/23926"}
                      type={"POSTER"}
                      title={"2024 Tandem Meetings of ASTCT® and CIBMTR® Abstract 537"}
                    />
                  </Col>
                  <Col lg={4} sm={6} className="two-col">
                    <CardTile
                      url={"https://tandem.confex.com/tandem/2024/meetingapp.cgi/Paper/23805"}
                      type={"POSTER"}
                      title={"2024 Tandem Meetings of ASTCT® and CIBMTR® Abstract 535"}
                    />
                  </Col>
                  <Col lg={3} sm={6} className="two-col">
                    <CardTile
                      url={"https://ashpublications.org/blood/article/140/Supplement%201/10794/490080/Cost-Effectiveness-Analysis-of-Motixafortide"}
                      type={"PRESENTATION"}
                      title={"ASH 2022 Abstract"}
                    />
                  </Col>
                  <Col lg={4} sm={6} className="two-col">
                    <CardTile
                      url={"https://ashpublications.org/blood/article/138/Supplement%201/475/479346/Motixafortide-BL-8040-and-G-CSF-Versus-Placebo-and"}
                      type={"PRESENTATION"}
                      title={"ASH 2021 Abstract"}
                    />
                  </Col>
                  <Col lg={4} sm={6} className="two-col">
                    <CardTile
                      url={"https://ashpublications.org/blood/article/138/Supplement%201/475/479346/Motixafortide-BL-8040-and-G-CSF-Versus-Placebo-and"}
                      type={"POSTER"}
                      title={"ASH 2021 Abstract"}
                    />
                  </Col>
                  <Col lg={3} sm={6} className="two-col">
                    <CardTile
                      url={"https://ashpublications.org/blood/article/138/Supplement%201/2849/481327/Hematopoietic-Cell-Transplantation-of-Higher-CD34"}
                      type={"POSTER"}
                      title={"ASH 2021 Abstract"}
                    />
                  </Col>
                  <Col lg={4} sm={6} className="two-col">
                    <CardTile
                      url={"https://ashpublications.org/blood/article/138/Supplement%201/3816/482602/Immunophenotypic-and-Single-Cell-Transcriptional"}
                      type={"POSTER"}
                      title={"ASH 2021 Abstract"}
                    />
                  </Col>
                  <Col lg={4} sm={6} className="two-col">
                    <CardTile
                      url={"https://ir.biolinerx.com/static-files/1d79195b-809e-492f-b95f-e06773028303"}
                      type={"POSTER"}
                      title={"AACR 2021"}
                    />
                  </Col>
                </Row>
              </TabPanel>

              <TabPanel>
                <Row>
                  <Col lg={3} md={4} sm={6} className="two-col">
                    <CardTile
                      url={"https://www.nature.com/articles/s41591-023-02273-z"}
                      type={"PUBLICATION"}
                      title={"Crees 2023"}
                    />
                  </Col>
                </Row>
              </TabPanel>
            </ResponsiveTabs>
          </div>

          <div className="page-reference-block">
            {/* <ul className="page-reference-block__list">
              <li>
                <sup>i</sup>Dhakal B, et al. Transplant Cell Ther. 2022;28(6):284-293.
              </li>
              <li>
                <sup>ii</sup>Giralt S, Costa L, Schriber J, et al. Biol Blood Marrow Transplant. 2014;20(3):295-308.
              </li>
              <li>
                <sup>iii</sup>Swan D, Hayden PJ, Eikema D-J, et al. BR J Haematol. 2022;197(1):82-96.
              </li>
              <li>
                <sup>iv</sup>Morris CL, Siegel E, Barlogie B, et al. Br J Haematol. 2003;120(3):413-423.
              </li>
              <li>
                <sup>v</sup>Bolon YT, Atshan R, Allbee-Johnson M, Estrada-Merly N, Lee SJ. Current use and outcome of hematopoietic stem cell transplantation: CIBMTR summary slides, 2022. Accessed August 8, 2023. https://cibmtr.org/Files/Summary-Slides--Reports/The-US-Summary-Slides-2022-v3---web-version.pptx.
              </li>
              <li>
                <sup>vi</sup>Hulin C, Offner F, Moreau P, et al. Haematologica. 2021;106(8):2257-2260.
              </li>
              <li>
                <sup>vii</sup>Chhabra S, Callander N, Watts NL, et al. Transplant Cell Ther. 2023;29(3):174.e1-174.e10.
              </li>
              <li>
                <sup>viii</sup>Edmisson J, et al. Poster presented at: 64th American Society of Hematology Annual Meeting and Exposition; December 10-13, 2022; New Orleans, LA.
              </li>
              <li>
                <sup>ix</sup>DiPersio JF, et al. Blood. 2009; 113(23):5720-6.
              </li>
              <li>
                <sup>x</sup>Shaughnessy P, et al. Biol Blood Marrow Transplant. 2013;19(9):1301-1309.
              </li>
              <li>
                <sup>xi</sup>Giralt S, Costa L, Schriber J, et al. Biol Blood Marrow Transplant. 2014;20(3):295-308.
              </li>
              <li>
                <sup>xii</sup>Chhabra S, Callander N, Watts NL, et al. Transplant Cell Ther.  2023;29(3):174.e1-174.e10.
              </li>
              <li>
                <sup>xiii</sup>APHEXDA. Prescribing information. BioLineRx Ltd; 2023.
              </li>
            </ul> */}
            <p><span className="bold">References: 1.</span> Dhakal B, Shah N, Kansagra A, et al. ASTCT clinical practice recommendations for transplantation and cellular therapies in multiple myeloma. <span className="italic">Transplant Cell Ther.</span> 2022;28(6):284-293. <span className="bold">2.</span> Giralt S, Costa L, Schriber J, et al. Optimizing autologous stem cell mobilization strategies to improve patient outcomes: consensus guidelines and recommendations. <span className="italic">Biol Blood Marrow Transplant.</span> 2014;20(3):295-308. <span className="bold">3.</span> Swan D, Hayden PJ, Eikema DJ, et al. Trends in autologous stem cell transplantation for newly diagnosed multiple myeloma: changing demographics and outcomes in European Society for Blood and Marrow Transplantation centres from 1995 to 2019. <span className="italic">Br J Haematol.</span> 2022;197(1):82-96. <span className="bold">4.</span> Morris CL, Siegel E, Barlogie B, et al. Mobilization of CD34+ cells in elderly patients (&gt;/= 70 years) with multiple myeloma: influence of age, prior therapy, platelet count and mobilization regimen. <span className="italic">Br J Haematol.</span> 2003;120(3):413-423. <span className="bold">5.</span> Bolon YT, Atshan R, Allbee-Johnson M, Estrada-Merly N, Lee SJ. Current use and outcome of hematopoietic stem cell transplantation: CIBMTR summary slides, 2022. Accessed August 8, 2023. https://cibmtr.org/Files/Summary-Slides--Reports/The-US-Summary-Slides-2022-v3---web-version.pptx. <span className="bold">6.</span> Hulin C, Offner F, Moreau P, et al. Stem cell yield and transplantation in transplant-eligible newly diagnosed multiple myeloma patients receiving daratumumab + bortezomib/thalidomide/dexamethasone in the phase 3 CASSIOPEIA study. <span className="italic">Haematologica.</span> 2021;106(8):2257-2260. <span className="bold">7.</span> Chhabra S, Callander N, Watts NL, et al. Stem cell mobilization yields with daratumumab- and lenalidomide-containing quadruplet induction therapy in newly diagnosed multiple myeloma: findings from the MASTER and GRIFFIN trials. <span className="italic">Transplant Cell Ther.</span> 2023;29(3):174.e1-174.e10. <span className="bold">8.</span> DiPersio JF, Stadtmauer EA, Nademanee A, et al. Plerixafor and G-CSF versus placebo and G-CSF to mobilize hematopoietic stem cells for autologous stem cell transplantation in patients with multiple myeloma. <span className="italic">Blood.</span> 2009;113(23):5720-5726. <span className="bold">9.</span> Edmisson J, et al. Poster presented at: 64th American Society of Hematology Annual Meeting and Exposition; December 10-13, 2022; New Orleans, LA. <span className="bold">10.</span> Shaughnessy P, Chao N, Shapiro J, et al. Pharmacoeconomics of hematopoietic stem cell mobilization: an overview of current evidence and gaps in the literature. Biol Blood Marrow Transplant. 2013;19(9):1301-1309. <span className="bold">11.</span> Giralt S, Costa L, Schriber J, et al. Optimizing autologous stem cell mobilization strategies to improve patient outcomes: consensus guidelines and recommendations. <span className="italic">Biol Blood Marrow Transplant.</span> 2014;20(3):295-308. <span className="bold">12.</span>&nbsp;APHEXDA. Prescribing information. BioLineRx Ltd; 2023.</p>
          </div>
        </div>
      </FluidContent>

      <FluidContent className="curved-wrap">
        <CurvedShape className="left-round" backgroundBg={magentaBg}>
          <h5>
            The partner of choice for drug development
          </h5>
          <p>Discover how our partnerships drive our pipeline and expand the potential reach of therapeutic solutions.</p>
          <div className="btn-wrap">
            <Button
              to="/partnerships/"
              className="btn-secondary has-icon-right"
              external={false}
            >
              PARTNER WITH US
              <FontAwesomeIcon icon={faArrowRight} />
            </Button>
          </div>
        </CurvedShape>
      </FluidContent>
    </HcpLayout >
  );
};
export default MotixafortideHscPage;

export const Head = () => (
  <Seo
    title="BioLineRx | Stem Cell Mobilization for Multiple Myeloma"
    description=" Learn about how motixafortide works in stem cell mobilization for multiple myeloma."
  />
);